// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { Sparkles } from "lucide-react";
import { type KeyboardEvent, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { AssistantPanel } from "@/features/assistant/assistant-panel";
import { useAssistant } from "@/features/assistant/assistant-provider";
import { assistantStatus } from "@/features/assistant/assistant-reducer";
import { publishFootprint } from "@/features/assistant/footprint";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

// One fixed element morphs between the 48px button and the panel: width, height, radius
// and background transition together while the icon and the panel body cross-fade.
// The body is laid out at the target size from the start, so the morph clips it rather
// than reflowing it.

const MORPH =
  "transition-[width,height,border-radius,background-color,right,bottom] duration-[350ms] ease-[cubic-bezier(.32,.72,0,1)] motion-reduce:transition-opacity";

const SIZE = {
  default: "h-[min(600px,calc(100dvh-2rem))] w-[min(400px,calc(100vw-2rem))]",
  expanded: "h-[calc(100dvh-2rem)] w-[min(720px,calc(100vw-2rem))]",
  mobile: "h-dvh w-screen",
} as const;

const PANEL_WIDTH_PX = { default: 400, expanded: 720 } as const;

export function AssistantLauncher() {
  const { t } = useTranslation();
  const { state, open, close, toggle } = useAssistant();
  const isMobile = useIsMobile();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const wasOpen = useRef(state.isOpen);
  const status = assistantStatus(state);
  const working = status === "working" || status === "streaming";
  const size = isMobile ? "mobile" : state.isExpanded ? "expanded" : "default";

  useEffect(
    function toggleOnShortcut() {
      function onKeyDown(e: globalThis.KeyboardEvent) {
        // Autofill and some extensions dispatch keydown without a `key`; a held key auto-repeats.
        if (typeof e.key !== "string" || e.repeat || e.defaultPrevented) return;
        if (e.key.toLowerCase() !== "j" || !(e.metaKey || e.ctrlKey) || e.altKey || e.shiftKey) return;
        e.preventDefault();
        toggle();
      }
      document.addEventListener("keydown", onKeyDown);
      return () => document.removeEventListener("keydown", onKeyDown);
    },
    [toggle],
  );

  useEffect(
    function moveFocusWithPanel() {
      if (state.isOpen && !wasOpen.current) composerRef.current?.focus();
      if (!state.isOpen && wasOpen.current) buttonRef.current?.focus();
      wasOpen.current = state.isOpen;
    },
    [state.isOpen],
  );

  useEffect(
    function publishLauncherFootprint() {
      function publish() {
        if (!state.isOpen) publishFootprint({ kind: "button" });
        else if (size === "mobile") publishFootprint({ kind: "fullscreen" });
        else {
          const viewportWidth = window.innerWidth;
          publishFootprint({ kind: "panel", width: Math.min(PANEL_WIDTH_PX[size], viewportWidth - 32), viewportWidth });
        }
      }
      publish();
      // The room left of the panel for toasts depends on the viewport width.
      window.addEventListener("resize", publish);
      return () => window.removeEventListener("resize", publish);
    },
    [state.isOpen, size],
  );

  useEffect(function hideFootprintOnUnmount() {
    return () => publishFootprint({ kind: "hidden" });
  }, []);

  function onPanelKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    // Esc that cancels an IME composition (or was handled by an inner widget) isn't for us.
    if (e.key !== "Escape" || e.nativeEvent.isComposing || e.defaultPrevented) return;
    e.stopPropagation();
    close();
  }

  return (
    <div
      data-state={state.isOpen ? "open" : "closed"}
      className={cn(
        "fixed z-50 overflow-hidden border shadow-lg",
        MORPH,
        state.isOpen ? "bg-popover text-popover-foreground" : "border-transparent bg-primary text-primary-foreground",
        state.isOpen && size === "mobile" ? "right-0 bottom-0 rounded-none" : "right-4 bottom-4",
        state.isOpen ? SIZE[size] : "size-12 rounded-full",
        state.isOpen && size !== "mobile" && "rounded-2xl",
      )}
    >
      <div
        role="dialog"
        aria-label={t("assistant.title")}
        aria-hidden={!state.isOpen}
        inert={!state.isOpen}
        onKeyDown={onPanelKeyDown}
        className={cn(
          "absolute right-0 bottom-0 transition-opacity",
          SIZE[size],
          state.isOpen ? "opacity-100 delay-100 duration-200" : "pointer-events-none opacity-0 duration-100",
        )}
      >
        <AssistantPanel composerRef={composerRef} />
      </div>

      <button
        ref={buttonRef}
        type="button"
        aria-label={t("assistant.open")}
        aria-expanded={state.isOpen}
        inert={state.isOpen}
        onClick={open}
        className={cn(
          "absolute right-0 bottom-0 flex size-12 items-center justify-center rounded-full outline-none transition-opacity focus-visible:ring-[3px] focus-visible:ring-inset focus-visible:ring-primary-foreground/70",
          state.isOpen ? "pointer-events-none opacity-0 duration-100" : "opacity-100 delay-150 duration-200",
        )}
      >
        <Sparkles className="size-5" />
        {!state.isOpen && working && (
          <span
            role="status"
            aria-label={t("assistant.status.working")}
            className="absolute inset-1 animate-spin rounded-full border-2 border-primary-foreground/70 border-r-transparent motion-reduce:animate-none"
          />
        )}
        {!state.isOpen && !working && state.hasUnread && (
          <span
            role="status"
            aria-label={t("assistant.status.unread")}
            className="absolute top-1.5 right-1.5 size-2.5 rounded-full bg-destructive ring-2 ring-primary"
          />
        )}
      </button>
    </div>
  );
}
